import type {
  IImportErrorDetails,
  IImportMainError,
  IImportRowError,
  IImportRowErrors,
  ImportErrorCode,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";

export const normalizeImportErrors = (
  errors: ImportErrorCode | ImportErrorCode[] | null | undefined
): ImportErrorCode[] => {
  if (Array.isArray(errors)) {
    return errors;
  }

  return errors ? [errors] : [];
};

export const getSortedRowErrorEntries = (
  rowErrors: IImportRowErrors
): [string, IImportRowError[]][] =>
  Object.entries(rowErrors).sort(([firstRow], [secondRow]) => {
    const firstRowNumber = Number(firstRow);
    const secondRowNumber = Number(secondRow);

    if (Number.isNaN(firstRowNumber) || Number.isNaN(secondRowNumber)) {
      return firstRow.localeCompare(secondRow);
    }

    return firstRowNumber - secondRowNumber;
  });

// Main errors are grouped by error code and columns only, so this is their identity
const getRowErrorKey = (error: IImportRowError): string =>
  JSON.stringify([
    error.errorCode,
    (error.columns ?? []).map((column) => column.name).sort(),
  ]);

/**
 * Removes row errors already summarised by `mainErrors`, so they are not
 * rendered once per row. Rows left without any error are dropped.
 */
export const excludeMainErrorsFromRowErrors = (
  rowErrors: IImportRowErrors,
  mainErrors: IImportMainError[] | null | undefined
): IImportRowErrors => {
  if (!mainErrors?.length) {
    return rowErrors;
  }

  const mainErrorKeys = new Set(
    mainErrors.map(({ error }) => getRowErrorKey(error))
  );

  return Object.fromEntries(
    Object.entries(rowErrors)
      .map(([row, errors]): [string, IImportRowError[]] => [
        row,
        errors.filter((error) => !mainErrorKeys.has(getRowErrorKey(error))),
      ])
      .filter(([, remainingErrors]) => remainingErrors.length > 0)
  );
};

export interface IDuplicateRelation {
  rows: string[];
  hiddenCount: number;
}

export const getDuplicateRelation = (
  errorDetails: IImportErrorDetails | null | undefined
): IDuplicateRelation => {
  const { duplicatedWithRows, duplicatedWithRowsCount } = errorDetails ?? {};
  let rows: string[] = [];

  if (Array.isArray(duplicatedWithRows)) {
    rows = duplicatedWithRows.map(String);
  }

  if (typeof duplicatedWithRows === "number") {
    rows = [String(duplicatedWithRows)];
  }

  rows = [...new Set(rows)].sort(
    (firstRow, secondRow) => Number(firstRow) - Number(secondRow)
  );

  const totalCount =
    typeof duplicatedWithRowsCount === "number"
      ? duplicatedWithRowsCount
      : rows.length;

  return {
    rows,
    hiddenCount: Math.max(totalCount - rows.length, 0),
  };
};

export const hasExceededImportErrorLimit = (
  rowsWithErrorsCount: number | undefined,
  rowsWithErrorsLimit: number | undefined
): boolean =>
  typeof rowsWithErrorsCount === "number" &&
  typeof rowsWithErrorsLimit === "number" &&
  rowsWithErrorsCount > rowsWithErrorsLimit;
